import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { 
  CreditCard, 
  Check, 
  Crown, 
  Building2, 
  Users, 
  Star,
  Loader2,
  QrCode,
  FileText,
  AlertCircle,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { apiRequest } from "../lib/utils";
import { toast } from "../components/ToastProvider";

const Assinaturas = () => {
  const navigate = useNavigate();
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assinaturaAtual, setAssinaturaAtual] = useState(null);
  const [selectedPlano, setSelectedPlano] = useState(null);
  const [selectedCiclo, setSelectedCiclo] = useState("monthly");
  const [formaPagamento, setFormaPagamento] = useState("PIX");
  const [processing, setProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [rede, setRede] = useState(null);

  useEffect(() => {
    fetchPlanos();
    fetchAssinaturaAtual();
    apiRequest("/pagamentos/desconto-rede").then(setRede).catch(() => setRede(null));
  }, []);

  const fetchPlanos = async () => {
    try {
      const data = await apiRequest("/pagamentos/planos");
      setPlanos(data.planos || []);
    } catch (error) {
      console.error("Erro ao carregar planos:", error);
      toast.error("Erro ao carregar planos");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssinaturaAtual = async () => {
    try {
      const data = await apiRequest("/pagamentos/assinatura");
      setAssinaturaAtual(data.assinatura);
    } catch (error) {
      console.error("Erro ao carregar assinatura:", error);
    }
  };

  const handleSelectPlano = (plano) => {
    setSelectedPlano(plano);
    setShowConfirmDialog(true);
  };

  const handleConfirmAssinatura = async () => {
    if (!selectedPlano) return;

    setProcessing(true);
    try {
      const response = await apiRequest(
        `/pagamentos/assinatura?plano=${selectedPlano.id}&ciclo=${selectedCiclo}&forma_pagamento=${formaPagamento}`,
        { method: "POST" }
      );

      toast.success(response.message || "Assinatura criada com sucesso!");
      setShowConfirmDialog(false);
      fetchAssinaturaAtual();
    } catch (error) {
      console.error("Erro ao criar assinatura:", error);
      toast.error(error.message || "Erro ao criar assinatura");
    } finally {
      setProcessing(false);
    }
  };

  const getPrecoInfo = (plano) => {
    if (!plano?.precos?.[selectedCiclo]) return null;
    const base = plano.precos[selectedCiclo];
    const pct = rede?.desconto_percentual || 0;
    if (!pct) return base;
    const fator = 1 - pct / 100;
    return {
      ...base,
      preco_mensal_sem_rede: base.preco_mensal,
      preco_mensal: Math.round(base.preco_mensal * fator * 100) / 100,
      preco_total: Math.round(base.preco_total * fator * 100) / 100,
      desconto_rede_percentual: pct,
    };
  };

  const getPlanIcon = (planoId) => {
    switch (planoId) {
      case "basico": return <Building2 className="w-6 h-6" />;
      case "profissional": return <Star className="w-6 h-6" />;
      case "enterprise": return <Crown className="w-6 h-6" />;
      default: return <Building2 className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planoId) => {
    switch (planoId) {
      case "basico": return "border-slate-200 hover:border-slate-400";
      case "profissional": return "border-teal-200 hover:border-teal-400 bg-teal-50/30";
      case "enterprise": return "border-amber-200 hover:border-amber-400 bg-amber-50/30";
      default: return "border-slate-200";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "ACTIVE":
        return <Badge className="bg-green-100 text-green-700">Ativa</Badge>;
      case "PENDING":
        return <Badge className="bg-yellow-100 text-yellow-700">Pendente</Badge>;
      case "OVERDUE":
        return <Badge className="bg-red-100 text-red-700">Em Atraso</Badge>;
      case "CANCELLED":
        return <Badge className="bg-slate-100 text-slate-700">Cancelada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-800">Planos e Assinaturas</h1>
          <p className="text-slate-600 mt-2">
            Escolha o plano ideal para sua farmácia
          </p>
        </div>

        {/* Aviso: planos individuais por CNPJ */}
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Os planos são <strong>individuais por CNPJ</strong>. Cada farmácia (CNPJ) deve possuir sua própria assinatura ativa para utilizar o sistema.
          </AlertDescription>
        </Alert>

        {/* Assinatura Atual */}
        {assinaturaAtual && (
          <Card className="border-teal-200 bg-teal-50/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Sua Assinatura Atual</CardTitle>
                {getStatusBadge(assinaturaAtual.status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Plano</p>
                  <p className="font-semibold">{assinaturaAtual.plano_nome}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Valor Mensal</p>
                  <p className="font-semibold">R$ {assinaturaAtual.valor_mensal?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Forma de Pagamento</p>
                  <p className="font-semibold">{assinaturaAtual.forma_pagamento}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Próxima Cobrança</p>
                  <p className="font-semibold">
                    {assinaturaAtual.proxima_cobranca 
                      ? new Date(assinaturaAtual.proxima_cobranca).toLocaleDateString('pt-BR')
                      : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Seletor de Ciclo */}
        <div className="flex justify-center">
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            {[
              { id: "monthly", label: "Mensal" },
              { id: "quarterly", label: "Trimestral", badge: "-5%" },
              { id: "semi_annual", label: "Semestral", badge: "-10%" },
              { id: "annual", label: "Anual", badge: "-20%" },
            ].map((ciclo) => (
              <button
                key={ciclo.id}
                onClick={() => setSelectedCiclo(ciclo.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedCiclo === ciclo.id
                    ? "bg-white text-teal-700 shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                {ciclo.label}
                {ciclo.badge && (
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                    {ciclo.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Grid de Planos */}
        {rede?.desconto_percentual > 0 && (
          <Alert className="border-teal-200 bg-teal-50" data-testid="alerta-desconto-rede">
            <AlertDescription className="text-teal-900">
              <strong>Desconto de rede aplicado:</strong> esta farmácia é o {rede.posicao}º CNPJ da rede{rede.matriz_nome ? ` de ${rede.matriz_nome}` : ""} e recebe <strong>{rede.desconto_percentual}% de desconto</strong> em todos os planos, somado ao desconto do ciclo.
            </AlertDescription>
          </Alert>
        )}
        <div className="grid md:grid-cols-3 gap-6">
          {planos.map((plano) => {
            const precoInfo = getPrecoInfo(plano);
            const isCurrentPlan = assinaturaAtual?.plano === plano.id && assinaturaAtual?.status === "ACTIVE";
            
            return (
              <Card 
                key={plano.id} 
                className={`relative transition-all duration-200 ${getPlanColor(plano.id)} ${
                  isCurrentPlan ? "ring-2 ring-teal-500" : ""
                }`}
              >
                {plano.id === "profissional" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-teal-600 text-white">Mais Popular</Badge>
                  </div>
                )}
                
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <Badge className="bg-green-600 text-white">Plano Atual</Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-2">
                  <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    plano.id === "basico" ? "bg-slate-100 text-slate-600" :
                    plano.id === "profissional" ? "bg-teal-100 text-teal-600" :
                    "bg-amber-100 text-amber-600"
                  }`}>
                    {getPlanIcon(plano.id)}
                  </div>
                  <CardTitle className="text-xl">{plano.nome}</CardTitle>
                  <CardDescription>{plano.descricao}</CardDescription>
                </CardHeader>

                <CardContent className="text-center">
                  <div className="mb-4">
                    {precoInfo?.desconto_rede_percentual > 0 && (
                      <p className="text-sm text-slate-400 line-through" data-testid={`preco-sem-rede-${plano.id}`}>R$ {precoInfo.preco_mensal_sem_rede.toFixed(2)}</p>
                    )}
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl font-bold text-slate-800" data-testid={`preco-mensal-${plano.id}`}>
                        R$ {precoInfo?.preco_mensal?.toFixed(2)}
                      </span>
                      <span className="text-slate-500">/mês</span>
                    </div>
                    {precoInfo?.desconto_percentual > 0 && (
                      <p className="text-sm text-green-600 mt-1">
                        Economia de {precoInfo.desconto_percentual}%
                      </p>
                    )}
                    {precoInfo?.desconto_rede_percentual > 0 && (
                      <p className="text-xs text-teal-700 font-medium mt-1" data-testid={`badge-rede-${plano.id}`}>
                        Desconto de rede: -{precoInfo.desconto_rede_percentual}%
                      </p>
                    )}
                    {precoInfo?.meses > 1 && (
                      <p className="text-xs text-slate-500 mt-1">
                        Total: R$ {precoInfo.preco_total?.toFixed(2)} ({precoInfo.meses} meses)
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 text-sm text-left mb-6">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Até {plano.max_usuarios} usuários</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Documentos ilimitados</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Verificações diárias</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Alertas por email</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Suporte por ticket</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Relatórios avançados</span>
                    </div>
                  </div>
                </CardContent>

                <CardFooter>
                  <Button 
                    className={`w-full ${
                      plano.id === "profissional" 
                        ? "bg-teal-600 hover:bg-teal-700" 
                        : plano.id === "enterprise"
                        ? "bg-amber-600 hover:bg-amber-700"
                        : ""
                    }`}
                    variant={plano.id === "basico" ? "outline" : "default"}
                    disabled={isCurrentPlan}
                    onClick={() => handleSelectPlano(plano)}
                  >
                    {isCurrentPlan ? "Plano Atual" : "Assinar Agora"}
                    {!isCurrentPlan && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Informações adicionais */}
        <div className="text-center text-sm text-slate-500">
          <p>Todos os planos incluem 7 dias de teste grátis</p>
          <p>Cancele a qualquer momento sem multa</p>
        </div>

        {/* Dialog de Confirmação */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirmar Assinatura</DialogTitle>
              <DialogDescription>
                Você está prestes a assinar o plano {selectedPlano?.nome}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Resumo */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Plano:</span>
                  <span className="font-semibold">{selectedPlano?.nome}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Ciclo:</span>
                  <span className="font-semibold">
                    {selectedCiclo === "monthly" ? "Mensal" :
                     selectedCiclo === "quarterly" ? "Trimestral" :
                     selectedCiclo === "semi_annual" ? "Semestral" : "Anual"}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Valor mensal:</span>
                  <span className="font-semibold">
                    R$ {getPrecoInfo(selectedPlano)?.preco_mensal?.toFixed(2)}
                  </span>
                </div>
                {getPrecoInfo(selectedPlano)?.desconto_rede_percentual > 0 && (
                  <div className="flex justify-between mb-2 text-teal-700 text-sm" data-testid="confirm-desconto-rede">
                    <span>Desconto de rede ({rede?.posicao}º CNPJ):</span>
                    <span>-{getPrecoInfo(selectedPlano).desconto_rede_percentual}%</span>
                  </div>
                )}
                {getPrecoInfo(selectedPlano)?.meses > 1 && (
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span className="text-slate-600">Total:</span>
                    <span className="font-bold text-lg">
                      R$ {getPrecoInfo(selectedPlano)?.preco_total?.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Forma de Pagamento */}
              <div>
                <Label className="mb-3 block">Forma de Pagamento</Label>
                <RadioGroup 
                  value={formaPagamento} 
                  onValueChange={setFormaPagamento}
                  className="grid grid-cols-3 gap-2"
                >
                  <div>
                    <RadioGroupItem value="PIX" id="pix" className="peer sr-only" />
                    <Label
                      htmlFor="pix"
                      className="flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-teal-600 [&:has([data-state=checked])]:border-teal-600 cursor-pointer"
                    >
                      <QrCode className="w-5 h-5 mb-1" />
                      <span className="text-xs">PIX</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="BOLETO" id="boleto" className="peer sr-only" />
                    <Label
                      htmlFor="boleto"
                      className="flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-teal-600 [&:has([data-state=checked])]:border-teal-600 cursor-pointer"
                    >
                      <FileText className="w-5 h-5 mb-1" />
                      <span className="text-xs">Boleto</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="CREDIT_CARD" id="card" className="peer sr-only" />
                    <Label
                      htmlFor="card"
                      className="flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-teal-600 [&:has([data-state=checked])]:border-teal-600 cursor-pointer"
                    >
                      <CreditCard className="w-5 h-5 mb-1" />
                      <span className="text-xs">Cartão</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  A cobrança será gerada imediatamente após a confirmação.
                </AlertDescription>
              </Alert>
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                onClick={handleConfirmAssinatura}
                disabled={processing}
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  "Confirmar Assinatura"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Assinaturas;
